import { z } from 'zod';
import { Taxi99ErrorCodeSchema } from './taxi99_errors.js';

const Latitude = z.number().min(-90).max(90);
const Longitude = z.number().min(-180).max(180);
export const NamedRefSchema = z.object({ id: z.string().min(1), name: z.string().min(1) }).strict();

/** `GET /internal/customers/:id/mobility-access` (customer-api → BFF e worker da Live
 *  Activity). `null` em cada lado = não resolvido (upstream fora, sem empresa, sem perfil).
 *  `mobilityPolicy` = a política `mobility` do `policyAccess` (D40: o worker não faz o
 *  lookup); opcional só para a customer-api de antes dela, que não a manda — ausente conta
 *  como não resolvido. */
export const MobilityAccessSchema = z
  .object({
    mobilityPolicy: z.boolean().nullable().optional(),
    smartMobility: z.boolean().nullable(),
    featureFlags: z.array(z.string()).nullable(),
  })
  .strict();
export type MobilityAccess = z.infer<typeof MobilityAccessSchema>;

/** `GET /mobility/places?q=` — endereços de mobilidade (voll-places, kind MOBILITY). */
export const Mobility99PlaceSchema = z
  .object({
    ref: z.string().min(1),
    name: z.string().min(1),
    address: z.string().nullable(),
    /** Só `true`: o resultado é uma rua sem número; a tela pede o número antes de usá-la. */
    needsNumber: z.literal(true).optional(),
  })
  .strict();
export type Mobility99Place = z.infer<typeof Mobility99PlaceSchema>;
export const Mobility99PlacesResponseSchema = z
  .object({ places: z.array(Mobility99PlaceSchema) })
  .strict();

/** `GET /mobility/places/reverse` — o endereço da coordenada e o país (checagem do Brasil). */
export const Mobility99ReversePlaceSchema = z
  .object({
    name: z.string().min(1),
    address: z.string().nullable(),
    latitude: Latitude,
    longitude: Longitude,
    countryCode: z.string().length(2).nullable(),
  })
  .strict();
export type Mobility99ReversePlace = z.infer<typeof Mobility99ReversePlaceSchema>;
export const Mobility99ReverseResponseSchema = z
  .object({ place: Mobility99ReversePlaceSchema.nullable() })
  .strict();

/** `GET /mobility/taxi99/defaults` — a cobrança inicial JÁ COM OS NOMES (validada
 *  contra o formulário fresco do checkout) e o telefone do perfil. `phoneLocked` só
 *  quando o telefone é um celular brasileiro válido (o app trava qualquer um).
 *
 *  `source` diz de onde a cobrança veio, e o rótulo da folha segue isso à risca
 *  (#4004): `trip` = a viagem de onde a corrida foi aberta (`?tripId=`, snapshot de
 *  checkout da trip); `last_ride` = a última corrida do viajante, SÓ quando o motivo
 *  dela veio junto; `default` = o centro de custo padrão do viajante (o motivo, quando
 *  vem, é o da compra mais recente ou o único do formulário; senão a folha pede);
 *  `none` = sem prefill (a folha abre o "Alterar"). */
export const Taxi99DefaultsSchema = z
  .object({
    prefill: z
      .object({
        company: NamedRefSchema,
        costCenter: NamedRefSchema,
        project: NamedRefSchema.optional(),
        motive: NamedRefSchema.optional(),
      })
      .strict()
      .nullable(),
    source: z.enum(['trip', 'last_ride', 'default', 'none']),
    phone: z.string().nullable(),
    phoneLocked: z.boolean(),
  })
  .strict();
export type Taxi99Defaults = z.infer<typeof Taxi99DefaultsSchema>;

const Label = z.string().trim().min(1).max(200);
/** O endereço que a folha mostrou sob o nome do lugar: vai para os recentes do viajante e,
 *  junto com o nome, para a 99 — é ele que traz a rua e o número ao motorista (#6213). */
const Address = z.string().trim().min(1).max(300);

/** Uma ponta da corrida: coordenada (GPS, hotel da viagem) ou um ref do voll-places.
 *  A 99 recebe `label` + `address` como a rua da ponta (o BFF completa o que faltar com o
 *  nome do voll-places e o endereço do geocode). */
export const Taxi99LocationSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('coords'),
      latitude: Latitude,
      longitude: Longitude,
      label: Label.optional(),
      address: Address.optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('place'),
      ref: z.string().min(1).max(200),
      label: Label.optional(),
      address: Address.optional(),
    })
    .strict(),
]);
export type Taxi99Location = z.infer<typeof Taxi99LocationSchema>;

/** `POST /mobility/taxi99/rides`. Empresa, centro de custo, projeto e motivo vão com o
 *  NOME, como o `CheckoutSubmit` do checkout: o `MobilityTaxi99Approval` exige `{id,name}`
 *  e um centro de custo de busca remota (`allCostCenters`) não está na lista do formulário.
 *  Quem valida o par é o legado, como no checkout. */
export const Taxi99RideRequestBodySchema = z
  .object({
    from: Taxi99LocationSchema,
    to: Taxi99LocationSchema,
    allocation: z
      .object({
        company: NamedRefSchema,
        costCenter: NamedRefSchema,
        project: NamedRefSchema.optional(),
      })
      .strict(),
    motive: NamedRefSchema,
    observation: z.string().max(300).optional(),
    phone: z.string().trim().min(8).max(24),
    /** A viagem de onde a folha foi aberta, a mesma do `GET /defaults?tripId=` (#6138). */
    tripId: z.string().trim().min(1).max(100).optional(),
  })
  .strict();
export type Taxi99RideRequestBody = z.infer<typeof Taxi99RideRequestBodySchema>;

export const Taxi99RideCreatedSchema = z
  .object({ h5Url: z.url(), voucherId: z.string().min(1) })
  .strict();
export type Taxi99RideCreated = z.infer<typeof Taxi99RideCreatedSchema>;

export const Taxi99ErrorBodySchema = z.object({ error: Taxi99ErrorCodeSchema }).strict();

const RideEnd = z.object({ street: z.string().nullable() }).strict();

/** A etapa da corrida da 99, derivada do `rawStatus` do webhook (live-activity). `ended` =
 *  concluída ou cancelada pelo viajante (motorista que desiste ou falta de motorista é `finding`:
 *  a 99 procura outro, #6799); só circula entre o worker e o BFF. */
export const Taxi99RideStageSchema = z.enum([
  'finding',
  'on_the_way',
  'arrived',
  'in_progress',
  'ended',
]);
export type Taxi99RideStage = z.infer<typeof Taxi99RideStageSchema>;
export const Taxi99ActiveRideStageSchema = Taxi99RideStageSchema.exclude(['ended']);
export type Taxi99ActiveRideStage = z.infer<typeof Taxi99ActiveRideStageSchema>;

/** O motorista como o 99-gateway o mapeia (sem cor nem foto). Sem telefone: nenhuma tela usa. */
export const Taxi99RideDriverSchema = z
  .object({
    name: z.string().min(1),
    plate: z.string().min(1).nullable(),
    model: z.string().min(1).nullable(),
  })
  .strict();
export type Taxi99RideDriver = z.infer<typeof Taxi99RideDriverSchema>;

/** `GET /internal/taxi99-rides/:externalRideId` (live-activity → BFF). */
export const Taxi99RideTrackingSchema = z
  .object({ stage: Taxi99RideStageSchema, driver: Taxi99RideDriverSchema.nullable() })
  .strict();
export type Taxi99RideTracking = z.infer<typeof Taxi99RideTrackingSchema>;

/** A corrida da 99 em andamento: `rideFlow == TAXI99` com `webviewUrl` (a regra do app).
 *  `stage`/`driver` AUSENTES = a faixa do live-activity desligada (o card de antes); `null` =
 *  ligada, sem leitura. Opcionais porque um web-chat em cache valida com `.strict()`. */
export const Taxi99ActiveRideSchema = z
  .object({
    voucherId: z.string().nullable(),
    orderId: z.string().nullable(),
    origin: RideEnd,
    destination: RideEnd,
    distanceKm: z.number().nonnegative().nullable(),
    webviewUrl: z.url(),
    stage: Taxi99ActiveRideStageSchema.nullable().optional(),
    driver: Taxi99RideDriverSchema.nullable().optional(),
  })
  .strict();
export type Taxi99ActiveRide = z.infer<typeof Taxi99ActiveRideSchema>;
export const Taxi99ActiveRideResponseSchema = z
  .object({ ride: Taxi99ActiveRideSchema.nullable() })
  .strict();

export const Taxi99HelpCenterResponseSchema = z.object({ h5Url: z.url() }).strict();

/** Um atalho de destino da folha "Pedir corrida": um destino recente (de uma corrida
 *  pedida) ou um Lugar favorito do viajante. `ref` (o do voll-places) só no recente
 *  que veio da busca; sem ele, a folha pede pela coordenada com o `title` como rua. */
export const Taxi99PlaceShortcutSchema = z
  .object({
    ref: z.string().min(1).optional(),
    title: z.string().min(1),
    subtitle: z.string().min(1).nullable(),
    latitude: Latitude,
    longitude: Longitude,
  })
  .strict();
export type Taxi99PlaceShortcut = z.infer<typeof Taxi99PlaceShortcutSchema>;

/** `GET /mobility/taxi99/places/shortcuts` — os destinos recentes (o mais novo
 *  primeiro) e os Lugares favoritos. Cada lista degrada sozinha para `[]`. */
export const Taxi99PlaceShortcutsResponseSchema = z
  .object({
    recent: z.array(Taxi99PlaceShortcutSchema),
    favorite: z.array(Taxi99PlaceShortcutSchema),
  })
  .strict();
export type Taxi99PlaceShortcutsResponse = z.infer<typeof Taxi99PlaceShortcutsResponseSchema>;
